import React, { useState, useEffect } from "react";
import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import leftArrowIcon from "../img/left_arrow_chat.svg";
import { trackNaverAppGuideView } from "../utils/naverConversion.js";

const AppGuidePage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1, 2, 3번 화면

  /* 비디오들은 S3 버킷에 업로드해둔 후 객체 URL로 가져옴 */
  const videoUrls = [
    "https://lawlow-front-assets.s3.ap-northeast-2.amazonaws.com/app-guide-feature1.mov",
    "https://lawlow-front-assets.s3.ap-northeast-2.amazonaws.com/app-guide-feature2.mov",
    "https://lawlow-front-assets.s3.ap-northeast-2.amazonaws.com/app-guide-feature3.mov",
  ];

  useEffect(() => {
    trackNaverAppGuideView({ id: "app-guide" });
  }, []);

  // 가이드를 이미 본 사용자는 메인으로 리디렉트
  useEffect(() => {
    const hasSeenGuide = localStorage.getItem("hasSeenAppGuide");
    if (hasSeenGuide === "true") {
      navigate("/main", { replace: true });
    }
  }, [navigate]);

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      // 가이드 완료 표시 후 메인으로 이동
      localStorage.setItem("hasSeenAppGuide", "true");
      navigate("/main");
    }
  };

  const handleFinish = () => {
    // 가이드 종료 시에도 완료 표시
    localStorage.setItem("hasSeenAppGuide", "true");
    navigate("/main");
  };

  return (
    <Container>
      <ContentWrapper>
        {/* Header - PC에서는 TextPanel 위, Mobile에서는 VideoPanel 위 */}
        <Header>
          {currentStep > 1 && (
            <BackButton onClick={handleBack}>
              <ArrowIcon src={leftArrowIcon} alt="뒤로가기" />
            </BackButton>
          )}
          <PageIndicator>{currentStep} / 3</PageIndicator>
        </Header>

        {/* 왼쪽 패널 - 가이드 */}
        <TextPanel>
          <ContentContainer>
            <TextContent>
              <Title>
                {currentStep === 1 && <>대화를 통해 내 상황을 <br/>더욱 정확하게 찾아보세요</>}
                {currentStep === 2 && <>내 상황과 가장 비슷한 판례  <br/>결과들을 한눈에 확인하세요</>}
                {currentStep === 3 && <>내 상황의 맥락까지 고려해 <br/>더욱 똑똑하게 검색해 보세요</>}
              </Title>
              <Description>
                {currentStep === 1 && <>실제 상황을 바탕으로 대화를 이어가며 <br/>내 사건과 비슷한 판례를 더 정확하게 찾아보세요.</>}
                {currentStep === 2 && <>내 상황과 가장 유사한 최대 5개 판례의 요약과, <br/>각 판례 결과의 자세한 내용을 확인해보세요.</>}
                {currentStep === 3 && <>내 상황의 세부 내용과 의도를 반영해 <br/>검색 결과를 더 정교하게 맞춤화해드립니다.</>}
              </Description>
            </TextContent>

            <ButtonContainer>
              <NextButton onClick={handleNext}>다음으로 넘어가기</NextButton>
              <FinishLink onClick={handleFinish}>가이드 종료하기</FinishLink>
            </ButtonContainer>
          </ContentContainer>
        </TextPanel>

        {/* 오른쪽 패널 - 비디오 자리 */}
        <VideoPanel>
          {/* 모바일에서만 Header 표시 */}
          <MobileHeader>
            {currentStep > 1 && (
              <BackButton onClick={handleBack}>
                <ArrowIcon src={leftArrowIcon} alt="뒤로가기" />
              </BackButton>
            )}
            <PageIndicator>{currentStep} / 3</PageIndicator>
          </MobileHeader>
          <VideoContainer>
            <VideoPlaceholder>
              {videoUrls.map((url, index) => (
                <Video
                  key={index}
                  src={url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  $isActive={currentStep === index + 1}
                />
              ))}
            </VideoPlaceholder>
          </VideoContainer>
        </VideoPanel>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f7f7f7;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 24px;
  box-sizing: border-box;

  @media (max-width: 599px) {
    padding: 24px 24px 33px;
    align-items: flex-start;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  // max-width: 1400px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center; /* PC에서 수직 중앙 정렬 */
  height: 100%;
  position: relative;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-auto-flow: row; 
    gap: 32px;
    align-items: start;
  }

  @media (max-width: 599px) {
    gap: 24px;
    padding: 0;
    grid-auto-flow: row;
    align-items: start;
    width: 100%;
    box-sizing: border-box;
  }
`;

const TextPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 24px;
  height: 100%; /* PC에서 VideoPanel과 같은 높이로 맞춤 */
  position: relative;
  grid-column: 1; /* PC에서 왼쪽 열 */
  justify-content: center; /* 내부 컨텐츠 수직 중앙 정렬 */

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 360px;
    order: 3; /* 모바일에서 가장 아래로 */
    padding-top: 0;
    grid-column: 1;
    align-self: center; /* Tablet에서 가운데 정렬 */
    margin: 0 auto; /* 가운데 정렬을 위한 추가 */
    height: auto; /* 태블릿/모바일에서는 자동 높이 */
    justify-content: flex-start;
  }

  @media (max-width: 599px) {
    gap: 20px;
    order: 3; /* 모바일에서 가장 아래로 */
    width: 100%;
    max-width: 100%;
    padding: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  grid-column: 1; /* PC에서 왼쪽 열 */
  position: absolute;
  top: 0px;
  left: 42px;
  z-index: 10; /* 다른 요소 위에 표시 */

  /* 모바일에서는 숨김 */
  @media (max-width: 1024px) {
    display: none;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  // margin-top: 100px;
  // width: 100%;
  flex: 1;
  padding: 0 24px;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 599px) {
    padding: 0 16px;
  }
`;

const TextContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
`;

const BackButton = styled.button`
  border: 0.3px solid rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;

  @media (max-width: 599px) {
    width: 32px;
    height: 32px;
  }

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    background-color: #e5e5e5;
  }
`;

const ArrowIcon = styled.img`
  width: 42px;
  height: 42px;
  pointer-events: none; /* 아이콘이 클릭 이벤트를 가로채지 않도록 */

  @media (max-width: 599px) {
    width: 32px;
    height: 32px;
  }
`;

const PageIndicator = styled.span`
  color: rgba(0, 0, 0, 0.6);
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.32px;
  background-color: rgba(0, 0, 0, 0.05);
  border: 0.3px solid rgba(0, 0, 0, 0.2);
  border-radius: 24px;
  padding: 8px 24px;

  @media (max-width: 599px) {
    font-size: 13px;
    line-height: 23px; 
    letter-spacing: -0.26px;
    padding: 4px 12px;
  }
`;

const Title = styled.h1`
  color: #000000;
  font-size: 32px;
  font-weight: 600;
  line-height: 46px;
  letter-spacing: -0.64px;
  margin: 0;
  text-align: center;
  width: 355px;
  margin-bottom: 16px;

  @media (max-width: 1024px) and (min-width: 600px) {
    font-size: 44px;
    line-height: 52px;
    letter-spacing: -0.8px;
    width: 490px;
    margin-bottom: 16px;
  }

  @media (max-width: 599px) {
    font-size: 24px;
    line-height: 34px;
    letter-spacing: -0.48px;
    width: 100%;
    max-width: 280px;
    margin-bottom: 8px;
  }
`;

const Description = styled.p`
  width: 332px;
  color: rgba(0, 0, 0, 0.8);
  font-size: 15px;
  font-weight: 600;
  line-height: 23px;
  letter-spacing: -0.3px;
  margin: 0;
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 1024px) and (min-width: 600px) {
    width: 450px;
    font-size: 24px;
    font-weight: 300;
    line-height: 34px;
    letter-spacing: -0.48px;
    margin-bottom: 40px;
  }

   @media (max-width: 599px) {
    width: 100%;
    max-width: 280px;
    font-size: 14px;
    letter-spacing: -0.28px;
    margin-bottom: 32px;
   }
`;

const NextButton = styled.button`
  width: 100%;
  max-width: 312px;
  height: 48px;
  margin-bottom: 24px;
  background-color: #000000;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 23px;
  letter-spacing: -0.28px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #333333;
  }

  &:active {
    background-color: #555555;
  }

  @media (max-width: 1024px) and (min-width: 600px) {
    font-size: 24px;
    line-height: 29px;
    letter-spacing: -0.48px;
    height: 56px;
    max-width: 360px;
    margin-bottom: 24px;
  }

  @media (max-width: 599px) {
    width: 100%;
    max-width: 280px;
    margin-bottom: 16px;
  }
`;

const FinishLink = styled.button`
  background: none;
  border: none;
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.26px;
  cursor: pointer;
  text-underline-offset: 3px;
  padding: 0;
  width: fit-content;
  transition: color 0.2s ease;

  &:hover {
    color: #000000;
  }

  @media (max-width: 1024px) and (min-width: 600px) {
    font-size: 24px;
    line-height: 29px;
    letter-spacing: -0.48px;
  }
`;

const VideoPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #ffffff;
  width: 100%;
  height: auto; /* 자동 높이로 변경 */
  max-height: calc(100vh - 52px); /* 상단 52px + 하단 52px + 기타 여백 = 156px */
  border-radius: 24px;
  box-shadow: 0 0 100px 10px rgba(0, 0, 0, 0.05);
  border: 0.5px solid rgba(0, 0, 0, 0.1);
  grid-column: 2; /* PC에서 오른쪽 열 */
  position: relative;
  overflow: hidden; /* 비디오가 border-radius 밖으로 나가지 않도록 */
  box-sizing: border-box;

  @media (max-width: 1024px) {
    order: 2; /* 모바일에서 Header 다음 (두 번째) */
    grid-column: 1;
    padding: 24px;
    width: 100%;
    height: auto;
    max-height: calc(100vh - 104px); /* Tablet도 뷰포트 높이에 맞춤 */
    box-sizing: border-box;
  }

  @media (max-width: 599px) {
    width: 100%;
    height: auto;
    max-height: calc(100vh - 150px); /* Mobile은 더 작은 여백 */
    order: 2; /* 모바일에서 Header 다음 (두 번째) */
    align-items: flex-start;
    padding: 18px;
    box-sizing: border-box;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;

  @media (max-width: 1024px) {
    height: 100%;
  }

  @media (max-width: 599px) {
    height: 100%;
  }
`;

const MobileHeader = styled.div`
  display: none; /* PC에서는 숨김 */

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 16px;
    width: 100%;
    // position: relative;
    // z-index: 10; /* 다른 요소 위에 표시 */
  }

  @media (max-width: 599px) {
    gap: 4px;
    margin-bottom: 12px;
  }
`;

const VideoPlaceholder = styled.div`
  flex: 1;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  overflow: hidden;
  border-radius: 24px; /* PC에서는 VideoPanel의 border-radius 사용 */
  min-height: 0; /* flex item이 부모의 높이를 넘지 않도록 */

  /* 모든 자식(비디오)을 같은 셀에 배치 */
  > * {
    grid-column: 1;
    grid-row: 1;
  }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain; /* 비디오가 컨테이너를 가득 채우도록 */
  pointer-events: none; /* 비디오 위의 모든 마우스 이벤트 차단 */
  opacity: ${props => props.$isActive ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
  visibility: ${props => props.$isActive ? 'visible' : 'hidden'};
`;

export default AppGuidePage;
